/**
 * The seam between the batch wizard (in schedule mode) and the schedule routes: the wizard's
 * selection → the `runConfig` a schedule replays, a stored `runConfig` → what the wizard is seeded
 * with, the create / preview bodies, the PATCH of only what changed, and which step a failure
 * belongs to. Pure, so every rule here is tested without rendering the page.
 *
 * Content fields go through `buildEnrichmentRunPayload`, the function batch create uses, so a
 * schedule and a batch built from the same choices can never read them differently.
 *
 * Which products a run takes is the filters plus the server's schedule defaults: `net_new`, the
 * worker's per-flow classes, no carry-over. The wizard offers no groups for a schedule, so it never
 * sends `ingestMode`, `includeClasses` or `includeNotEnrichedBuckets`, and an edit never changes
 * what a schedule stores for them.
 *
 * See `agentic-workflow-service/docs/api/schedule-run-config-parity-contract.md`.
 */

import { ApiError } from '~/api/errors';
import type { EnrichmentRunSelection } from '~/components/common/enrichment-run-panel/types';
import type { UseCaseType } from '~/features/content-enrichment/types/content-scoring.types';
import { buildEnrichmentRunPayload } from '~/features/content-enrichment/utils/enrichment-run-payload';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import { areAppliedFiltersEqual } from '~/features/experiments/utils/applied-filters';
import type {
  RunWizardContentSeed,
  RunWizardStepId,
} from '~/features/run-wizard/hooks/use-run-wizard';

import type {
  CreateScheduleRequestWire,
  ScheduleSourceWire,
  ScheduleUseCaseWire,
  UpdateScheduleRequestWire,
} from '../types/catalog-ops-schedule.api.types';
import type {
  CadenceDraft,
  OverlapPolicy,
  ScheduleDetail,
  ScheduleRunConfig,
  ScheduleRunConfigPatch,
} from '../types/catalog-ops-schedule.types';

import { cadenceDraftToSpec } from './schedule-cadence';

/** English is always produced; the stored locale lists may name it, the wizard's never do. */
const SOURCE_LOCALE = 'en-us';

/** The wizard's choices for a schedule, in the batch wizard's own terms. */
export interface ScheduleWizardSelection {
  /** The Product Filters step's applied filters. */
  filters: AppliedProductFilters;
  /** `wizard.selection()`: attributes, post-steps, scoring, prompt, languages. */
  run: EnrichmentRunSelection;
  /** Not offered by the wizard: carried untouched from the batch or schedule it was seeded
   *  from, so a seeded choice is never dropped. */
  tagsTrendsEnabled?: boolean | null;
  /** Stored values the wizard has no control for, replayed as they are (see `keptValuesOf`). */
  kept?: ScheduleKeptValues;
}

/** What a stored setup holds that this wizard cannot show, so it must not silently drop. */
export interface ScheduleKeptValues {
  /** Stored field keys the channel's taxonomy no longer offers; sent with the picked ones. */
  contentAttributes?: string[];
  /** The prompt, on a channel that offers no prompt picker (Meta). */
  retailer?: string | null;
  /** The languages, on a channel that offers no language picker (Meta). */
  targetLocales?: string[] | null;
}

/** The kept values of a stored setup for this wizard: field keys missing from `availableKeys`,
 *  and the prompt / languages when the wizard offers no control for them. */
export function keptValuesOf(
  config: ScheduleRunConfig,
  wizard: { availableKeys: readonly string[]; showRetailerPrompt: boolean; showLanguages: boolean },
): ScheduleKeptValues {
  const offered = new Set(wizard.availableKeys);
  const missing = (config.contentAttributes ?? []).filter((k) => !offered.has(k));
  return {
    // Only once the taxonomy is known: before that every key would read as missing.
    ...(offered.size > 0 && missing.length > 0 ? { contentAttributes: missing } : {}),
    ...(!wizard.showRetailerPrompt && config.retailer ? { retailer: config.retailer } : {}),
    ...(!wizard.showLanguages && config.targetLocales?.length
      ? { targetLocales: config.targetLocales }
      : {}),
  };
}

/**
 * The selection as the `runConfig` a schedule replays. Never carries a batch-only field: no
 * `syncId` (each run imports fresh), `overrideCache`, `idempotencyKey`, `plannedCost`,
 * `productClassCounts`, `maxProducts` or batch sizes — nor the product-group fields, which a
 * schedule leaves to the server's defaults.
 */
export function toScheduleRunConfig(selection: ScheduleWizardSelection): ScheduleRunConfigPatch {
  const { runConfig } = buildEnrichmentRunPayload(selection.run);
  const kept = selection.kept ?? {};
  const contentAttributes = [
    ...new Set([...(runConfig.contentAttributes ?? []), ...(kept.contentAttributes ?? [])]),
  ];
  const retailer = runConfig.retailer ?? kept.retailer ?? undefined;
  const targetLocales = runConfig.targetLocales ?? kept.targetLocales ?? undefined;
  return {
    filters: { ...selection.filters },
    ...(contentAttributes.length > 0 ? { contentAttributes } : {}),
    ...(runConfig.searchDescriptors ? { searchDescriptors: runConfig.searchDescriptors } : {}),
    ...(runConfig.scoring ? { scoring: runConfig.scoring } : {}),
    ...(retailer ? { retailer } : {}),
    ...(targetLocales?.length ? { targetLocales } : {}),
    ...(typeof selection.tagsTrendsEnabled === 'boolean'
      ? { tagsTrendsEnabled: selection.tagsTrendsEnabled }
      : {}),
  };
}

/** Where a schedule set up in the wizard comes from: its connector, and the batch it was
 *  seeded from (provenance only, the whole setup travels in `runConfig`). */
export interface ScheduleWizardSource {
  connectorCode: string;
  useCase: UseCaseType;
  fromJobId: string | null;
}

/** The §5.1 source block shared by preview and create. */
export function scheduleSourceOf(
  source: ScheduleWizardSource,
  runConfig: ScheduleRunConfigPatch,
): ScheduleSourceWire {
  return {
    connectorCode: source.connectorCode,
    useCase: source.useCase as ScheduleUseCaseWire,
    ...(source.fromJobId ? { fromJobId: source.fromJobId } : {}),
    runConfig,
  };
}

/** `POST /schedules` from the wizard (§5.1): no `jobId`, the whole setup in `runConfig`. */
export function buildScheduleCreateBody(args: {
  name: string;
  source: ScheduleWizardSource;
  selection: ScheduleWizardSelection;
  cadence: CadenceDraft;
  overlapPolicy: OverlapPolicy;
  catchupWindowSeconds: number;
}): CreateScheduleRequestWire {
  return {
    ...scheduleSourceOf(args.source, toScheduleRunConfig(args.selection)),
    name: args.name.trim(),
    scheduleSpec: cadenceDraftToSpec(args.cadence),
    overlapPolicy: args.overlapPolicy,
    catchupWindowSeconds: args.catchupWindowSeconds,
  };
}

/** A stored run config as the wizard takes it. */
export interface ScheduleWizardSeed {
  filters: AppliedProductFilters;
  content: RunWizardContentSeed;
  tagsTrendsEnabled: boolean | null;
}

/**
 * A stored `runConfig` (a schedule's, or the one preview resolves for a batch) as the wizard's
 * starting point: its filters and content. `null` keeps its "no explicit choice" meaning wherever
 * the wizard can show it: scoring renders On, every field is picked. The product-group fields are
 * not seeded: the wizard offers none for a schedule, and an edit keeps what is stored.
 */
export function seedFromRunConfig(config: ScheduleRunConfig): ScheduleWizardSeed {
  return {
    filters: { ...config.filters },
    content: {
      contentAttributes: config.contentAttributes ? [...config.contentAttributes] : null,
      searchDescriptors: config.searchDescriptors?.enabled ?? null,
      phraseDescriptions: config.searchDescriptors?.phraseDescriptions ?? null,
      scoring: {
        before: config.scoring?.preScoring ?? true,
        after: config.scoring?.postScoring ?? true,
      },
      retailer: config.retailer ?? '',
      locales: (config.targetLocales ?? []).filter((l) => l.toLowerCase() !== SOURCE_LOCALE),
    },
    tagsTrendsEnabled: config.tagsTrendsEnabled,
  };
}

/**
 * Fields a stored setup leaves at "no explicit choice" (`null`) while the wizard shows a concrete
 * value for them: scoring On, the taxonomy's descriptor default. Saving an edit stamps those
 * explicitly, so what the operator checked is what every run keeps.
 *
 * `contentAttributes: null` is left out on purpose: it means "every field, including ones the
 * channel adds later", which an explicit list would freeze. Prompt and languages read `null`
 * exactly as the wizard shows them (shared prompt, English only). The product-group fields are not
 * shown at all, so whatever the schedule stores for them is never touched.
 */
export type StampedField = 'scoring' | 'searchDescriptors';

export function storedNullFields(config: ScheduleRunConfig): StampedField[] {
  const out: StampedField[] = [];
  if (config.scoring?.preScoring == null || config.scoring.postScoring == null) {
    out.push('scoring');
  }
  if (
    config.searchDescriptors?.enabled == null ||
    config.searchDescriptors.phraseDescriptions == null
  ) {
    out.push('searchDescriptors');
  }
  return out;
}

/**
 * The edit baseline: the wizard's config right after it was filled from `stored` (so normalising a
 * stored value never reads as a change), without the fields `stored` left null — those then
 * always differ, and an unchanged save stamps the value the operator saw.
 *
 * Filters are the exception: their baseline is what the schedule stores, never what the wizard
 * showed. The server checks the required scope on the stored filters merged with the patch, so
 * any filter on screen that the schedule doesn't store yet (a required default the edit seed
 * added, or one picked before the seed settled) must be sent.
 */
export function editBaselineOf(
  stored: ScheduleRunConfig,
  seeded: ScheduleRunConfigPatch,
): ScheduleRunConfigPatch {
  const nulls = new Set<string>(storedNullFields(stored));
  const kept = Object.fromEntries(
    Object.entries(seeded).filter(([field]) => !nulls.has(field)),
  ) as ScheduleRunConfigPatch;
  return { ...kept, filters: { ...stored.filters } };
}

/** The stamped fields a PATCH carries: what Review says Save will record explicitly. */
export function stampedFieldsOf(
  stored: ScheduleRunConfig,
  patch: ScheduleRunConfigPatch,
): StampedField[] {
  return storedNullFields(stored).filter((field) => patch[field] !== undefined);
}

/** Same members, any order. */
function sameSet(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  const left = new Set(a ?? []);
  const right = new Set(b ?? []);
  return left.size === right.size && [...left].every((v) => right.has(v));
}

/**
 * The PATCH body for an edit: only the fields that differ from `baseline` (the wizard's own
 * config right after it was seeded from the schedule, so normalisation never reads as a change).
 * A cleared value is sent explicitly: `retailer: ''`, `targetLocales: []`. Never carries the
 * product-group fields (`ingestMode`, `includeClasses`, `includeNotEnrichedBuckets`): the schedule
 * keeps what it stores for them.
 */
export function diffScheduleRunConfig(
  baseline: ScheduleRunConfigPatch,
  current: ScheduleRunConfigPatch,
): ScheduleRunConfigPatch {
  const patch: ScheduleRunConfigPatch = {};
  const filters = (current.filters ?? {}) as AppliedProductFilters;
  if (!areAppliedFiltersEqual((baseline.filters ?? {}) as AppliedProductFilters, filters)) {
    patch.filters = filters;
  }
  if (
    current.contentAttributes !== undefined &&
    !sameSet(baseline.contentAttributes, current.contentAttributes)
  ) {
    patch.contentAttributes = current.contentAttributes;
  }
  if (
    current.searchDescriptors &&
    (baseline.searchDescriptors === undefined ||
      current.searchDescriptors.enabled !== baseline.searchDescriptors.enabled ||
      current.searchDescriptors.phraseDescriptions !==
        baseline.searchDescriptors.phraseDescriptions)
  ) {
    patch.searchDescriptors = current.searchDescriptors;
  }
  if (
    current.scoring &&
    (baseline.scoring === undefined ||
      current.scoring.preScoring !== baseline.scoring.preScoring ||
      current.scoring.postScoring !== baseline.scoring.postScoring)
  ) {
    patch.scoring = current.scoring;
  }
  if ((current.retailer ?? '') !== (baseline.retailer ?? '')) {
    patch.retailer = current.retailer ?? '';
  }
  if (!sameSet(baseline.targetLocales, current.targetLocales)) {
    patch.targetLocales = current.targetLocales ?? [];
  }
  if (
    current.tagsTrendsEnabled !== undefined &&
    current.tagsTrendsEnabled !== baseline.tagsTrendsEnabled
  ) {
    patch.tagsTrendsEnabled = current.tagsTrendsEnabled;
  }
  return patch;
}

// ---------------------------------------------------------------------------
// Failures → the step that owns them
// ---------------------------------------------------------------------------

/**
 * Which kind of failure, for the copy above the messages: a field or rule the server refused,
 * a duplicate of another schedule (create), a conflict saving an edit, the server unreachable,
 * an edit whose settings saved but whose rename did not (`partial`), or anything else.
 */
export type ScheduleWizardErrorKind =
  | 'invalid'
  | 'duplicate'
  | 'conflict'
  | 'network'
  | 'partial'
  | 'generic';

export interface ScheduleWizardError {
  /** The step whose controls the failure is about; unpinned failures land on Review. */
  step: RunWizardStepId;
  kind: ScheduleWizardErrorKind;
  /** The server's own operator-readable sentences for that step. */
  messages: string[];
}

/** Whether the failing call was a create / preview, or a save of an existing schedule. */
export type ScheduleWizardCall = 'create' | 'edit';

/** `runConfig.<field>` → the step that edits it. */
const RUN_CONFIG_FIELD_STEP: Record<string, RunWizardStepId> = {
  filters: 'products',
  ingestMode: 'products',
  includeClasses: 'products',
  includeNotEnrichedBuckets: 'products',
  contentAttributes: 'content',
  searchDescriptors: 'content',
  tagsTrendsEnabled: 'content',
  retailer: 'content',
  scoring: 'scoringLanguages',
  targetLocales: 'scoringLanguages',
};

/** Top-level request fields → the step that edits them. */
const BODY_FIELD_STEP: Record<string, RunWizardStepId> = {
  scheduleSpec: 'cadence',
  overlapPolicy: 'cadence',
  catchupWindowSeconds: 'cadence',
  name: 'review',
};

/** Rule failures with no field that are still about the Products step: the scope guardrail
 *  ("Missing required product scope filters for …") and a run that would take nothing. */
const PRODUCTS_RULE = /required product scope filters|would take no products/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A detail's `field` path (`runConfig.targetLocales[0]`, `scheduleSpec.cron`) → its step. */
export function stepOfField(field: string): RunWizardStepId {
  const path = field.replace(/^body\.?/, '');
  if (path.startsWith('runConfig.')) {
    const root = /^[A-Za-z]+/.exec(path.slice('runConfig.'.length))?.[0] ?? '';
    return RUN_CONFIG_FIELD_STEP[root] ?? 'review';
  }
  const root = /^[A-Za-z]+/.exec(path)?.[0] ?? '';
  return BODY_FIELD_STEP[root] ?? 'review';
}

/**
 * Pins a failed preview, create or save to a step.
 *
 * - 400 with `details[].field`: the earliest step any field belongs to, with that field's own
 *   sentence — its `message`, else the `errors[]` entries naming the field. A field with neither
 *   goes to the Review banner with the server's messages, never spread over a step.
 * - 400 without fields: the scope guardrail and takes-nothing rule go to Products, the rest
 *   (one-of, name) to Review.
 * - 409: on create the duplicate-schedule warning; on an edit a plain "couldn't save", both with
 *   the server's own message.
 * - The server unreachable (no response) reads as such, never as "Failed to fetch".
 */
export function readScheduleWizardError(
  err: unknown,
  steps: readonly RunWizardStepId[],
  call: ScheduleWizardCall = 'create',
): ScheduleWizardError {
  if (!(err instanceof ApiError)) {
    // fetch rejects with a TypeError when the request never got an answer.
    return err instanceof TypeError
      ? { step: 'review', kind: 'network', messages: [] }
      : {
          step: 'review',
          kind: 'generic',
          messages: err instanceof Error && err.message ? [err.message] : [],
        };
  }
  if (err.code === 'NETWORK_ERROR' || err.code === 'TIMEOUT') {
    return { step: 'review', kind: 'network', messages: [] };
  }
  if (err.statusCode === 409) {
    return {
      step: 'review',
      kind: call === 'edit' ? 'conflict' : 'duplicate',
      messages: err.messages,
    };
  }
  if (err.statusCode !== 400) return { step: 'review', kind: 'generic', messages: err.messages };

  const body = isRecord(err.body) ? err.body : {};
  const details = Array.isArray(body.details) ? body.details.filter(isRecord) : [];
  const errors = Array.isArray(body.errors)
    ? body.errors.filter((e): e is string => typeof e === 'string' && e.trim() !== '')
    : [];
  const pinned = details
    .map((d) => (typeof d.field === 'string' ? d.field : ''))
    .filter((field) => field !== '')
    .map((field) => {
      const own = details.find((d) => d.field === field)?.message;
      const named = errors.filter((e) => e.includes(field));
      return {
        field,
        step: stepOfField(field),
        messages: typeof own === 'string' && own.trim() !== '' ? [own] : named,
      };
    });

  if (pinned.length === 0) {
    const step = err.messages.some((m) => PRODUCTS_RULE.test(m)) ? 'products' : 'review';
    return { step, kind: 'invalid', messages: err.messages };
  }
  const first = pinned.reduce((a, b) => (steps.indexOf(b.step) < steps.indexOf(a.step) ? b : a));
  const messages = [
    ...new Set(pinned.filter((p) => p.step === first.step).flatMap((p) => p.messages)),
  ];
  // A field the server named without saying why can't be explained on its step.
  if (messages.length === 0) return { step: 'review', kind: 'invalid', messages: err.messages };
  return { step: first.step, kind: 'invalid', messages };
}

/** When an edited schedule runs: the Cadence step's values. */
export interface ScheduleTiming {
  draft: CadenceDraft;
  overlapPolicy: OverlapPolicy;
  /** `null` = Temporal's own default, as stored. */
  catchupWindowSeconds: number | null;
}

/** Whether the edited timing differs from the stored one. The cadence is compared as the spec
 *  the form would send on both sides, so a lossless round trip never reads as a change. */
export function timingChanged(detail: ScheduleDetail, timing: ScheduleTiming): boolean {
  return (
    JSON.stringify(cadenceDraftToSpec(timing.draft)) !==
      JSON.stringify(cadenceDraftToSpec(detail.cadence)) ||
    timing.overlapPolicy !== detail.overlapPolicy ||
    timing.catchupWindowSeconds !== detail.catchupWindowSeconds
  );
}

/**
 * The one `PUT /{id}` an edit sends when its timing or name changed: the Cadence step's spec,
 * overlap and catch-up with the name. A rename alone resends the stored timing verbatim
 * (`buildRenameBody`), so an untouched cadence is never round-tripped through the form.
 */
export function buildScheduleUpdateBody(
  detail: ScheduleDetail,
  name: string,
  timing: ScheduleTiming,
): UpdateScheduleRequestWire {
  if (!timingChanged(detail, timing)) return buildRenameBody(detail, name);
  return {
    name: name.trim(),
    scheduleSpec: cadenceDraftToSpec(timing.draft),
    overlapPolicy: timing.overlapPolicy,
    catchupWindowSeconds: timing.catchupWindowSeconds,
  };
}

/** `PUT /{id}` for a rename: the stored cadence resent verbatim, only the name changes. */
export function buildRenameBody(detail: ScheduleDetail, name: string): UpdateScheduleRequestWire {
  return {
    name: name.trim(),
    scheduleSpec: detail.spec,
    overlapPolicy: detail.overlapPolicy,
    catchupWindowSeconds: detail.catchupWindowSeconds,
  };
}

// ---------------------------------------------------------------------------
// Where the wizard lives
// ---------------------------------------------------------------------------

/** The schedule wizard's route for a platform; it lives beside batch creation's. */
export function scheduleWizardRoute(platform: 'google' | 'meta'): string {
  return `/enrichments/${platform}/schedule`;
}

/** A schedule's connector code (`GMC` / `META`) → the wizard platform that edits it. */
export function schedulePlatformOf(connector: string): 'google' | 'meta' {
  return connector.toUpperCase().startsWith('META') ? 'meta' : 'google';
}
